import type { S3Client } from "@aws-sdk/client-s3";

/**
 * A menu snapshot the backend wrote to S3, read once per Lambda container.
 *
 * WHY THIS EXISTS. A drain asks the guest site to re-render N item pages, and
 * `res.revalidate` is an HTTPS HEAD back through CloudFront, so every page is a
 * SEPARATE invocation that fetches its own data. Twenty paths is forty renders
 * and forty backend calls. Core already knows exactly what changed, so it can
 * write the answer once and let the renders read it instead of asking.
 *
 * The shape is deliberately IDENTICAL to `backendCalls.json`, the file the
 * BUILD already reads: the same `url` and `${url}/allItems` keys, so the two
 * paths consult one data shape rather than two. This is that file, for runtime.
 *
 * Inert unless the Lambda says otherwise. `CACHE_BUCKET_NAME` is injected by
 * the OpenNext zone module (cache_bucket_env_variables), so a local run, a CI
 * run and `next build` all see no bucket and fall straight through to the
 * backend -- which is exactly today's behaviour. There is no flag to set.
 */
type GuestSnapshot = Record<string, any>;

/**
 * The key core overwrites before each drain. Hardcoded on BOTH sides -- it must
 * match GUEST_SNAPSHOT_KEY in the backend's process/guestSite/guestSnapshot.ts.
 *
 * Deliberately NOT under `CACHE_BUCKET_KEY_PREFIX`. That resolves to `cache`
 * (`join("/", compact([s3_path_prefix, "cache", zone_suffix]))`) and is
 * OpenNext's own ISR namespace, which its cache handler owns and prunes -- not
 * somewhere to leave a file it does not know about. Core cannot know the prefix
 * either: it is a per-zone terraform output injected only into the Lambda, so a
 * shared constant is the only thing both ends can agree on.
 */
const SNAPSHOT_KEY = "revalidations_snapshot.json";

/**
 * ONE object, whose keys are the URLs a render asks for:
 *
 *   /restaurant/<id>               the config body      -- every page
 *   /restaurant/social-media/<id>  the social body      -- every page
 *   /category/details/<id>         the category list    -- /home
 *   /restaurant/<id>/allItems      [ item bodies ]      -- item pages
 *
 * Core writes it whole, immediately before the drain that reads it, so a drain
 * always reads its own write. A config sync writes the first three and drops
 * the items, which costs nothing: item pages are only rendered by menu drains,
 * and those put the items back first. Any key that is absent falls back to the
 * API, which is the behaviour that exists today.
 */

/**
 * ONE well-known key, not one per menu version.
 *
 * A render cannot be told which version to read: `res.revalidate` carries a
 * path and nothing else, and the invocation that received the payload is a
 * different process that is blocked awaiting this very request. So the render
 * can only look somewhere it already knows.
 *
 * Last write wins, and that is correct rather than merely tolerable: a drain
 * that reads a snapshot written by a LATER save renders newer data than it was
 * asked for, which is never worse than what it would have rendered. Each
 * snapshot is internally consistent because core writes it in one PUT.
 *
 * SIXTY seconds, matching the one-minute collector upstream: admin gathers a
 * minute of edits and fires them as one batch, so a container should see one
 * snapshot per batch and pay one GetObject for a whole drain.
 *
 * KNOWN WINDOW, accepted deliberately. The TTL starts when a container first
 * READS, not when a batch fires, and those are not aligned: a container that
 * cached batch A's snapshot partway through A's drain still holds it for the
 * remainder of the minute, so the first renders of batch B can read A's data
 * and store it as fresh. Nothing re-renders that page until the next change
 * touches it. The exposure is up to a minute of edits, and it needs a second
 * batch to land while a container from the first is still warm.
 *
 * If that bites, the fix is not a shorter TTL but a conditional GetObject:
 * revalidate the cached copy against its ETag, which costs a request and no
 * transfer and cannot serve an older snapshot than S3 holds.
 */
const HIT_TTL_MS = 60_000;

/**
 * A miss is remembered only briefly.
 *
 * Core writes the snapshot BEFORE it calls /api/revalidate, so a render should
 * find one. But a container that cached "no snapshot" from an earlier, snapshot
 * -less drain would keep missing for the whole TTL and quietly go on hammering
 * the backend. Five seconds is long enough that one chunk does not re-ask S3
 * per page, short enough that the next drain is seen.
 */
const MISS_TTL_MS = 5_000;

type Entry = { at: number; body: Promise<GuestSnapshot | null> };

let cached: Entry | undefined;
let client: S3Client | undefined;

/** Test seam, and what a long-lived container would want after a deploy. */
export const clearGuestSnapshot = (): void => {
  cached = undefined;
  client = undefined;
};

const read = async (): Promise<GuestSnapshot | null> => {
  const bucket = process.env.CACHE_BUCKET_NAME;

  if (!bucket) {
    return null;
  }

  /**
   * Loaded HERE, not at module scope.
   *
   * apiCacheCalls imports this file, so a static import would put the AWS SDK
   * behind every import of apiCacheCalls -- and jest does not transform
   * node_modules (`node_modules/(?!@fluentui)`), so @aws-sdk/core's ESM
   * `export const` is a SyntaxError that fails four unrelated suites. It also
   * means a restaurant with no bucket never pays to load the SDK at all.
   */
  const { GetObjectCommand, S3Client } = await import("@aws-sdk/client-s3");

  client =
    client ??
    new S3Client(
      process.env.CACHE_BUCKET_REGION
        ? { region: process.env.CACHE_BUCKET_REGION }
        : {}
    );

  const response = await client.send(
    new GetObjectCommand({ Bucket: bucket, Key: SNAPSHOT_KEY })
  );
  const body = await response.Body?.transformToString();
  const snapshot = body ? (JSON.parse(body) as GuestSnapshot) : null;

  /**
   * ONE line per GetObject, which is the number this cache exists to keep
   * small -- and the only way to measure it.
   *
   * There is nothing else to count. The bucket has no server access logging
   * and no request metrics, and S3 GetObject is a CloudTrail DATA event, which
   * is off. So the saving was only ever inferred from how many Lambda
   * containers happened to serve a drain; this states it.
   *
   * Deliberately HERE and not in loadGuestSnapshot: this runs once per
   * container per TTL, not once per render. A measured drain was 168 renders
   * across 12 containers in 28s, so this is ~12 lines, not 168. The per-render
   * log that used to sit on this path wrote 22,170 lines in one day on vasko,
   * which is the mistake this comment exists to not repeat.
   *
   * `keys` is what makes a line useful rather than decorative: a snapshot that
   * is present but missing the item bodies still reads as a hit while every
   * item page falls back to the API.
   */
  console.log(
    `PREPIT guest snapshot fetched keys=${
      snapshot ? Object.keys(snapshot).length : 0
    } bytes=${body?.length ?? 0} age=${
      response.LastModified
        ? Math.round((Date.now() - response.LastModified.getTime()) / 1000)
        : -1
    }s`
  );

  return snapshot;
};

/**
 * The snapshot for this container, or null to mean "ask the backend".
 *
 * NEVER THROWS. A missing, unreadable or malformed snapshot must degrade to the
 * behaviour that exists today, not fail a render: a page that fails to render
 * is a page core records as stale and retries, which is the loop this whole
 * change exists to shorten.
 *
 * Holds the PROMISE, not the value, so the first render in a container starts
 * one GetObject and every render behind it awaits that same one -- the pattern
 * `restaurantCache` in apiCacheCalls.ts already uses for the same reason.
 */
export const loadGuestSnapshot = async (): Promise<GuestSnapshot | null> => {
  const hit = cached;

  if (hit) {
    const age = Date.now() - hit.at;
    const resolved = await hit.body.catch(() => null);
    const ttl = resolved ? HIT_TTL_MS : MISS_TTL_MS;

    if (age < ttl) {
      return resolved;
    }
  }

  const entry: Entry = {
    at: Date.now(),
    body: read().catch((error) => {
      // Absent is the ordinary case while this rolls out, so it is not an
      // error worth shouting about -- but an UNREADABLE one is a
      // misconfiguration that would otherwise look like a cache that simply
      // never helps.
      const name = (error as { name?: string })?.name;

      if (name !== "NoSuchKey" && name !== "NotFound") {
        console.log(
          `PREPIT guest snapshot unreadable (${
            name ?? (error as Error)?.message
          })`
        );
      }

      return null;
    }),
  };

  cached = entry;

  return await entry.body;
};
